import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'
import { useCurrentFiscalYear } from '../hooks'
import type { FiscalYear } from '../types'

interface AccountingContextValue {
  residenceId: string
  selectedFiscalYear: FiscalYear | null
  setSelectedFiscalYear: (fy: FiscalYear | null) => void
  activeFiscalYear: FiscalYear | undefined
  isFiscalYearModalOpen: boolean
  setIsFiscalYearModalOpen: (open: boolean) => void
}

const AccountingContext = createContext<AccountingContextValue | null>(null)

export function AccountingProvider({
  children,
  residenceId,
}: {
  children: ReactNode
  residenceId: string
}) {
  const { data: currentFiscalYear } = useCurrentFiscalYear(residenceId)
  const [selectedFiscalYear, setSelectedFiscalYear] = useState<FiscalYear | null>(null)
  const [isFiscalYearModalOpen, setIsFiscalYearModalOpen] = useState(false)

  // Reset selected fiscal year when residence changes
  // This prevents showing stale data from a previous residence
  useEffect(() => {
    setSelectedFiscalYear(null)
  }, [residenceId])

  const activeFiscalYear = selectedFiscalYear ?? currentFiscalYear ?? undefined

  return (
    <AccountingContext.Provider
      value={{
        residenceId,
        selectedFiscalYear,
        setSelectedFiscalYear,
        activeFiscalYear,
        isFiscalYearModalOpen,
        setIsFiscalYearModalOpen,
      }}
    >
      {children}
    </AccountingContext.Provider>
  )
}

export function useAccountingContext() {
  const context = useContext(AccountingContext)
  if (!context) {
    throw new Error('useAccountingContext must be used within AccountingProvider')
  }
  return context
}
